import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  SimpleGrid,
  VStack,
  useToast,
  Heading,
  Text,
  useColorMode,
} from '@chakra-ui/react';
import { useMutation, gql } from '@apollo/client';
import { getColor, brandConfig } from '../brandConfig';

const SUBMIT_CONTACT_FORM = gql`
  mutation SubmitContactForm($input: SubmitContactFormInput!) {
    submitContactForm(input: $input) {
      id
      email
      createdAt
    }
  }
`;

interface ContactFormProps {
  title?: string;
  description?: string;
  moduleId?: string;
  submitButtonText?: string;
  selectedService?: string;
}

export const ContactForm: React.FC<ContactFormProps> = ({
  title = 'Get in Touch',
  description = 'Ready to work together? Send a message and I\'ll get back to you within one business day.',
  moduleId,
  submitButtonText = 'Send Message',
  selectedService,
}) => {
  const { colorMode } = useColorMode();
  const toast = useToast();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    organisation: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });

  useEffect(() => {
    if (selectedService) {
      setFormData((prev) => ({ ...prev, service: selectedService }));
    }
  }, [selectedService]);

  const [submitContactForm, { loading }] = useMutation(SUBMIT_CONTACT_FORM);

  const isLight = colorMode === 'light';
  const cardGradientBg = getColor('background.cardGradient', colorMode);
  const cardBorder = getColor('border.darkCard', colorMode);
  const textPrimary = getColor(isLight ? 'text.primary' : 'text.primaryDark', colorMode);
  const textSecondary = getColor(isLight ? 'text.secondary' : 'text.secondaryDark', colorMode);
  const fieldBg = getColor('background.card', colorMode);
  const fieldBorderColor = getColor('border.medium', colorMode);
  const fieldFocusColor = getColor('primary', colorMode);
  const fieldFocusShadow = isLight
    ? '0 0 0 3px rgba(139, 94, 60, 0.1)'
    : '0 0 0 3px rgba(201, 169, 124, 0.12)';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const submissionUrl = window.location.href;
      const serviceContext = formData.service ? `Service interest: ${formData.service}\n` : '';
      const orgContext = formData.organisation ? `Organisation: ${formData.organisation}\n` : '';
      const moduleContext = moduleId ? `Module: ${moduleId}\n` : '';
      const messageWithContext = `${moduleContext}${serviceContext}${orgContext}\n${formData.message}`.trim();

      await submitContactForm({
        variables: {
          input: {
            firstName: formData.firstName,
            lastName: '',
            email: formData.email,
            phone: formData.phone,
            message: messageWithContext,
            submissionUrl,
            notificationEmails: [brandConfig.contact.email || 'hello@ljamessolutions.com.au'],
            formType: 'contact',
          },
        },
        context: {
          headers: {
            'x-tenant-id': brandConfig.tenantId,
          },
        },
      });

      toast({
        title: 'Message sent!',
        description: 'Thank you for your inquiry. We\'ll be in touch soon.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        organisation: '',
        email: '',
        phone: '',
        service: '',
        message: '',
      });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast({
        title: 'Error',
        description: `Failed to send message. Please try again or email us directly at ${brandConfig.contact.email || 'hello@ljamessolutions.com.au'}`,
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <Box
      bg={cardGradientBg}
      backdropFilter="blur(10px)"
      boxShadow={isLight ? '0 4px 24px rgba(24, 20, 15, 0.08)' : '0 8px 32px 0 rgba(0, 0, 0, 0.37)'}
      borderColor={cardBorder}
      borderWidth="1px"
      borderRadius="none"
      p={8}
      maxW="600px"
      mx="auto"
    >
      <VStack spacing={6} align="stretch">
        <Box textAlign="center">
          <Heading size="lg" color={textPrimary} mb={2}>
            {title}
          </Heading>
          <Text color={textSecondary}>{description}</Text>
        </Box>

        <form onSubmit={handleSubmit}>
          <VStack spacing={4}>
            <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4} w="full">
              <FormControl isRequired>
                <FormLabel color={textPrimary} fontSize="0.72rem" fontWeight="500" letterSpacing="0.12em" textTransform="uppercase">
                  Name
                </FormLabel>
                <Input
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="Your name"
                  bg={fieldBg}
                  borderColor={fieldBorderColor}
                  borderRadius="none"
                  focusBorderColor={fieldFocusColor}
                  _hover={{ borderColor: fieldFocusColor }}
                  _focus={{ borderColor: fieldFocusColor, boxShadow: fieldFocusShadow }}
                />
              </FormControl>

              <FormControl>
                <FormLabel color={textPrimary} fontSize="0.72rem" fontWeight="500" letterSpacing="0.12em" textTransform="uppercase">
                  Organisation
                </FormLabel>
                <Input
                  name="organisation"
                  value={formData.organisation}
                  onChange={handleChange}
                  placeholder="Your organisation"
                  bg={fieldBg}
                  borderColor={fieldBorderColor}
                  borderRadius="none"
                  focusBorderColor={fieldFocusColor}
                  _hover={{ borderColor: fieldFocusColor }}
                  _focus={{ borderColor: fieldFocusColor, boxShadow: fieldFocusShadow }}
                />
              </FormControl>
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4} w="full">
              <FormControl isRequired>
                <FormLabel color={textPrimary} fontSize="0.72rem" fontWeight="500" letterSpacing="0.12em" textTransform="uppercase">
                  Email
                </FormLabel>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  bg={fieldBg}
                  borderColor={fieldBorderColor}
                  borderRadius="none"
                  focusBorderColor={fieldFocusColor}
                  _hover={{ borderColor: fieldFocusColor }}
                  _focus={{ borderColor: fieldFocusColor, boxShadow: fieldFocusShadow }}
                />
              </FormControl>
              <FormControl>
                <FormLabel color={textPrimary} fontSize="0.72rem" fontWeight="500" letterSpacing="0.12em" textTransform="uppercase">
                  Phone
                </FormLabel>
                <Input
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Your phone number"
                  bg={fieldBg}
                  borderColor={fieldBorderColor}
                  borderRadius="none"
                  focusBorderColor={fieldFocusColor}
                  _hover={{ borderColor: fieldFocusColor }}
                  _focus={{ borderColor: fieldFocusColor, boxShadow: fieldFocusShadow }}
                />
              </FormControl>
            </SimpleGrid>

            <FormControl>
              <FormLabel color={textPrimary} fontSize="0.72rem" fontWeight="500" letterSpacing="0.12em" textTransform="uppercase">
                I'm interested in
              </FormLabel>
              <Select
                name="service"
                value={formData.service}
                onChange={handleChange}
                bg={fieldBg}
                borderColor={fieldBorderColor}
                borderRadius="none"
                color={formData.service ? textPrimary : undefined}
                focusBorderColor={fieldFocusColor}
                _hover={{ borderColor: fieldFocusColor }}
                _focus={{ borderColor: fieldFocusColor, boxShadow: fieldFocusShadow }}
              >
                <option value="">Select a service...</option>
                <option value="Change, Readiness & Transformation">Change, Readiness &amp; Transformation</option>
                <option value="Strategic Communication">Strategic Communication</option>
                <option value="Employee Experience & Capability">Employee Experience &amp; Capability</option>
                <option value="Strategic Advisory">Strategic Advisory</option>
                <option value="Not sure, let's talk">Not sure, let's talk</option>
              </Select>
            </FormControl>

            <FormControl isRequired>
              <FormLabel color={textPrimary} fontSize="0.72rem" fontWeight="500" letterSpacing="0.12em" textTransform="uppercase">
                Message
              </FormLabel>
              <Textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell me a little about what you're working on..."
                rows={5}
                bg={fieldBg}
                borderColor={fieldBorderColor}
                borderRadius="none"
                focusBorderColor={fieldFocusColor}
                _hover={{ borderColor: fieldFocusColor }}
                _focus={{ borderColor: fieldFocusColor, boxShadow: fieldFocusShadow }}
              />
            </FormControl>

            <Button
              type="submit"
              size="lg"
              width="100%"
              isLoading={loading}
              loadingText="Sending..."
              bg={fieldFocusColor}
              color={isLight ? '#fdfaf5' : '#18140f'}
              borderRadius="none"
              fontWeight="500"
              fontSize="0.85rem"
              letterSpacing="0.1em"
              textTransform="uppercase"
              border={`1.5px solid ${fieldFocusColor}`}
              _hover={{ bg: getColor('primaryHover', colorMode), borderColor: getColor('primaryHover', colorMode) }}
            >
              {submitButtonText}
            </Button>
          </VStack>
        </form>
      </VStack>
    </Box>
  );
};
